/**
 * Polyfill for the TC39 "Upsert" proposal (Stage 4):
 *   - Map.prototype.getOrInsert
 *   - Map.prototype.getOrInsertComputed
 *   - WeakMap.prototype.getOrInsert
 *   - WeakMap.prototype.getOrInsertComputed
 *
 * Shipped in V8 138+ and Safari 26+. Earlier Safari (and any other engine
 * that predates the proposal) throws `TypeError: ... is not a function`.
 *
 * `pdfjs-dist@5.x` calls `Map.prototype.getOrInsertComputed` at 11 sites
 * without polyfilling — so the document viewer crashes immediately on
 * Safari 17/18. This module is imported at the top of
 * `src/instrumentation-client.ts`, which Next.js guarantees to execute
 * before any other client code (including the dynamic pdfjs import).
 *
 * Semantics follow the spec draft: https://tc39.es/proposal-upsert/
 *
 * Implementation notes:
 * - Map and WeakMap get independent installations that each dispatch
 *   through their own intrinsic `has`/`get`/`set` captured at module load.
 *   Native `Map.prototype.has` brand-checks its receiver via the internal
 *   `[[MapData]]` slot, so mis-calls (`Map.prototype.getOrInsert.call(wm, …)`
 *   or `getOrInsertComputed` on an object literal) throw the same
 *   `TypeError: Method Map.prototype.has called on incompatible receiver`
 *   the native method would — subclass overrides of `has/get/set` likewise
 *   cannot redirect behaviour.
 * - Re-entrancy contract: if the callback synchronously sets the key
 *   before returning, the callback's return value still wins — matches
 *   V8 behaviour and is what pdfjs-dist relies on.
 */

function defineIfMissing(
  target: object,
  name: string,
  value: (this: object, ...args: unknown[]) => unknown,
): void {
  // Feature-detect on the prototype; never overwrite a native implementation
  // (engines may ship with subtle differences we don't want to mask).
  if (typeof (target as Record<string, unknown>)[name] === 'function') return;
  Object.defineProperty(target, name, {
    value,
    writable: true,
    configurable: true,
    enumerable: false,
  });
}

if (typeof Map !== 'undefined') {
  // Capture intrinsics at install time so subclass `has/get/set` overrides
  // (or later global monkey-patching) cannot alter semantics. Calling these
  // with a non-Map receiver triggers the engine's native brand check.
  const MapHas = Map.prototype.has;
  const MapGet = Map.prototype.get;
  const MapSet = Map.prototype.set;

  function getOrInsert<K, V>(this: Map<K, V>, key: K, value: V): V {
    if (MapHas.call(this, key)) return MapGet.call(this, key) as V;
    MapSet.call(this, key, value);
    return value;
  }

  function getOrInsertComputed<K, V>(this: Map<K, V>, key: K, callback: (key: K) => V): V {
    if (typeof callback !== 'function') {
      throw new TypeError('getOrInsertComputed: callback must be callable');
    }
    if (MapHas.call(this, key)) return MapGet.call(this, key) as V;
    const value = callback(key);
    // Per spec step 8: set (and thus overwrite) after the callback returns.
    // If the callback re-entered and set the key, the callback's return value
    // wins — matching V8.
    MapSet.call(this, key, value);
    return value;
  }

  defineIfMissing(
    Map.prototype,
    'getOrInsert',
    getOrInsert as (this: object, ...args: unknown[]) => unknown,
  );
  defineIfMissing(
    Map.prototype,
    'getOrInsertComputed',
    getOrInsertComputed as (this: object, ...args: unknown[]) => unknown,
  );
}

if (typeof WeakMap !== 'undefined') {
  const WeakMapHas = WeakMap.prototype.has;
  const WeakMapGet = WeakMap.prototype.get;
  const WeakMapSet = WeakMap.prototype.set;

  function getOrInsert<K extends WeakKey, V>(this: WeakMap<K, V>, key: K, value: V): V {
    if (WeakMapHas.call(this, key)) return WeakMapGet.call(this, key) as V;
    WeakMapSet.call(this, key, value);
    return value;
  }

  function getOrInsertComputed<K extends WeakKey, V>(
    this: WeakMap<K, V>,
    key: K,
    callback: (key: K) => V,
  ): V {
    if (typeof callback !== 'function') {
      throw new TypeError('getOrInsertComputed: callback must be callable');
    }
    if (WeakMapHas.call(this, key)) return WeakMapGet.call(this, key) as V;
    const value = callback(key);
    WeakMapSet.call(this, key, value);
    return value;
  }

  defineIfMissing(
    WeakMap.prototype,
    'getOrInsert',
    getOrInsert as (this: object, ...args: unknown[]) => unknown,
  );
  defineIfMissing(
    WeakMap.prototype,
    'getOrInsertComputed',
    getOrInsertComputed as (this: object, ...args: unknown[]) => unknown,
  );
}

// Augment the lib types so callers can use the methods without `as any`.
declare global {
  interface Map<K, V> {
    getOrInsert(key: K, value: V): V;
    getOrInsertComputed(key: K, callback: (key: K) => V): V;
  }
  interface WeakMap<K extends WeakKey, V> {
    getOrInsert(key: K, value: V): V;
    getOrInsertComputed(key: K, callback: (key: K) => V): V;
  }
}

export {};
